import { memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRegisterSW } from "virtual:pwa-register/react";
import { debugValue } from "@/env/utils";
import { Dialog } from "@/lib/capacitor/dialog";
import { useMe } from "@/lib/hooks/use-me";
import { OrganizationAutoSwitchProvider } from "@/lib/providers/organization-auto-switch";
import { PermissionsProvider } from "@/lib/providers/permission";
import { PostHogProvider } from "@/lib/providers/posthog/components/Provider";
import { ConfigProvider } from "@/lib/providers/posthog/config";
import { QueryProvider } from "@/lib/providers/query/provider";
import { SessionProvider } from "@/lib/providers/session";
import { ThemeProvider } from "@/lib/providers/theme/provider";
import { WebSocketProvider } from "@/lib/providers/websocket";
import { StoreProvider } from "@/lib/store/provider";
import { Compose } from "@/utils/components/compose";

export function Providers(props: React.PropsWithChildren) {
  const { t } = useTranslation();

  const { updateServiceWorker } = useRegisterSW({
    immediate: true,
    onNeedRefresh,
  });

  function onNeedRefresh() {
    Dialog.confirm({
      title: t("pwa.on-need-refresh.title"),
      message: t("pwa.on-need-refresh.message"),
      okButtonTitle: t("pwa.on-need-refresh.ok"),
      cancelButtonTitle: t("pwa.on-need-refresh.cancel"),
    }).then((result) => {
      if (!result) {
        return;
      }
      updateServiceWorker();
    });
  }

  return (
    <Compose
      providers={[
        <QueryProvider key="react-query" />,
        <ThemeProvider key="theme" />,
        <SessionProvider key="session" />,
        <StoreProvider key="zustand" />,
        <WebSocketProvider key="websocket" />,
        <PostHogProvider key="posthog" />,
        <ConfigProvider key="config" />,
        <DevelopmentProvider key="development" />,
        <DataProvider key="data" />,
        // <OrganizationAutoSwitchProvider key="organization-auto-switch" />,
        <PermissionsProvider key="permissions" />,
      ]}
      {...props}
    />
  );
}

function DevelopmentProvider(props: React.PropsWithChildren) {
  const className = "debug_ui_touch-target";

  useEffect(() => {
    if (debugValue("ui:touch-target")) {
      document.documentElement.classList.add(className);
    } else {
      document.documentElement.classList.remove(className);
    }

    return () => {
      document.documentElement.classList.remove(className);
    };
  }, []);

  return <>{props.children}</>;
}

const DataProvider = memo(function DataProvider(props: React.PropsWithChildren) {
  useMe();

  return <>{props.children}</>;
});
